<template>
  <div class="min-h-screen bg-[#f8fafc] font-sans">
    
    <!-- Top Navigation Bar -->
    <nav class="bg-white border-b border-gray-200 sticky top-0 z-50">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">

    <div
      class="flex items-center gap-3 cursor-pointer"
      @click="$router.push('/')"
    >
      <!-- GetBoarded Logo -->
      <img
        src="@/assets/images/aboutUs/gbicon.svg"
        alt="GetBoarded"
        class="w-8 h-8 object-contain"
      />

      <span class="font-bold text-xl text-gray-900">
        GetBoarded
      </span>
    </div>

    <vs-button
      type="border"
      color="#eab308"
      class="font-semibold"
      @click="$router.push('/')"
    >
      Create Profile
    </vs-button>

  </div>
</nav>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      
      <!-- LOADING & ERROR STATES -->
      <div v-if="loading" class="flex flex-col justify-center items-center h-64 gap-3">
        <svg class="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-500 font-medium">Loading organization...</span>
      </div>

      <div v-else-if="error" class="flex justify-center items-center h-64">
        <div class="text-center bg-white p-8 rounded-xl shadow-sm border border-gray-200 max-w-md w-full">
          <feather-icon icon="LockIcon" svgClasses="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 class="text-xl font-bold text-gray-800 mb-2">Profile Unavailable</h2>
          <p class="text-gray-500 mb-6">{{ error }}</p>
        </div>
      </div>

      <!-- MAIN PROFILE VIEW -->
      <div v-else-if="orgData" class="space-y-6">
        
        <!-- 1. SLIM IDENTITY HEADER -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div class="flex flex-col md:flex-row items-start md:items-center gap-6">
            <!-- Logo -->
            <div class="p-1 border border-gray-100 rounded-lg shadow-sm bg-white shrink-0">
              <vs-avatar
                v-if="orgData.logo"
                class="m-0" size="100px"
                :src="constants.s3Bucket + '/organization/logo/' + orgData.logo"
              />
              <vs-avatar
                v-else
                class="m-0 bg-gray-50" size="100px"
                :src="require('@/assets/images/icons/organization/business_black_36dp.svg')"
              />
            </div>
            
            <!-- Core Identity Info -->
            <div>
              <h1 class="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
                {{ orgData.name || orgData.companyName }}
              </h1>
              
              <div class="flex flex-wrap items-center gap-3 text-sm text-gray-600 font-medium">
                <span v-if="orgData.organizationType" class="flex items-center gap-1">
                  <feather-icon icon="BriefcaseIcon" svgClasses="w-4 h-4 text-gray-400" />
                  {{ orgData.organizationType }}
                </span>
                <span v-if="orgData.organizationType && orgData.location" class="text-gray-300">•</span>
                <span v-if="orgData.location" class="flex items-center gap-1">
                  <feather-icon icon="MapPinIcon" svgClasses="w-4 h-4 text-gray-400" />
                  {{ orgData.location }}
                </span>
              </div>
            </div>
          </div>

          <!-- Primary Action -->
          <div class="w-full md:w-auto flex flex-col gap-3 shrink-0">
            <vs-button v-if="orgData.website" :href="formatUrl(orgData.website)" target="_blank" color="primary" class="w-full font-bold shadow-sm">
              Visit Website
            </vs-button>
            <vs-button v-if="orgData.workEmail" :href="`mailto:${orgData.workEmail}`" type="border" color="primary" class="w-full font-semibold">
              Contact Us
            </vs-button>
          </div>
        </div>

        <!-- 2. TWO-COLUMN LAYOUT -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          <!-- MAIN CONTENT (Left side) -->
          <div class="lg:col-span-2 space-y-6">
            
            <!-- About Section -->
            <div v-if="orgData.about" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
              <h2 class="text-lg font-bold text-gray-900 mb-4 border-b border-gray-100 pb-3">About Organization</h2>
              <div class="text-gray-700 leading-relaxed whitespace-pre-line text-[15px]">
                {{ orgData.about }}
              </div>
            </div>

            <!-- Meet the Team (Leadership) -->
            <div v-if="orgData.leadership && orgData.leadership.length > 0" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
              <h2 class="text-lg font-bold text-gray-900 mb-5 border-b border-gray-100 pb-3">Meet the Team</h2>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div 
                  v-for="(leader, idx) in orgData.leadership" :key="idx"
                  class="flex items-center gap-4 p-4 border border-gray-100 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition-colors"
                >
                  <vs-avatar size="56px" :src="getAvatarUrl(leader)" class="m-0 shadow-sm border border-gray-200" />
                  <div>
                    <p class="font-bold text-left text-gray-900">{{ leader.firstName }} {{ leader.lastName }}</p>
                    <p class="text-xs text-gray-500 text-left font-semibold uppercase tracking-wider mt-0.5">{{ leader.role }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Open Opportunities -->
            <div v-if="orgData.openRoles && orgData.openRoles.length > 0" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
              <div class="flex items-center justify-between mb-5 border-b border-gray-100 pb-3">
                <h2 class="text-lg font-bold text-gray-900">Open Opportunities</h2>
                <span class="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">{{ orgData.openRoles.length }} Actively Hiring</span>
              </div>
              
              <div class="space-y-4">
                
                <div 
                  v-for="(role, idx) in paginatedRoles" :key="idx"
                  class="group border border-gray-100 rounded-lg p-5 hover:shadow-md hover:border-primary/40 transition-all duration-200 bg-white flex flex-col gap-4"
                >
                  <!-- Top Row: Title, Desc & Button -->
                  <div class="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div class="flex-grow">
                      <!-- Title -->
                      <div class="flex items-center gap-2">
                        <feather-icon icon="BriefcaseIcon" class="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                        <h3 class="font-bold text-[17px] text-gray-900 group-hover:text-primary transition-colors">{{ role.title }}</h3>
                      </div>
                      
                      <!-- Description (if present) -->
                      <p v-if="role.description" class="text-sm text-gray-500 mt-2 line-clamp-2 pr-4 leading-relaxed">
                        {{ role.description }}
                      </p>
                    </div>
                    
                    <!-- Action Button -->
                    <!-- <div class="shrink-0 mt-2 sm:mt-0 border-t border-gray-50 sm:border-none pt-3 sm:pt-0">
                      <vs-button 
                        color="primary" 
                        type="border" 
                        class="w-full sm:w-auto font-semibold px-6 py-2 rounded-md bg-primary/5 hover:bg-primary hover:text-white transition-colors" 
                        @click="$router.push('/')"
                      >
                        View Role
                      </vs-button>
                    </div> -->
                  </div>
                  
                  <!-- Bottom Row: Skills & Soft Skills -->
                  <div class="flex flex-col gap-3 mt-1 pt-3 border-t border-gray-50" v-if="(role.skills && role.skills.length) || (role.softSkills && role.softSkills.length)">
                    
                    <!-- Core Skills -->
                    <div class="flex items-start gap-2" v-if="role.skills && role.skills.length">
                      <span class="text-xs font-semibold text-gray-400 mt-1 w-16 shrink-0 uppercase tracking-wide">Skills</span>
                      <div class="flex flex-wrap gap-2">
                        <span 
                          v-for="skill in role.skills.slice(0, 5)" :key="'hard-'+skill" 
                          class="bg-blue-50/60 text-blue-600 border border-blue-100/60 px-2.5 py-0.5 rounded text-[11.5px] font-semibold"
                        >
                          {{ skill }}
                        </span>
                        <span v-if="role.skills.length > 5" class="text-gray-400 text-xs font-medium py-0.5 px-1">
                          +{{ role.skills.length - 5 }} more
                        </span>
                      </div>
                    </div>

                    <!-- Soft Skills -->
                    <div class="flex items-start gap-2" v-if="role.softSkills && role.softSkills.length">
                      <span class="text-xs font-semibold text-gray-400 mt-1 w-16 shrink-0 uppercase tracking-wide">Soft Skills</span>
                      <div class="flex flex-wrap gap-2">
                        <span 
                          v-for="skill in role.softSkills.slice(0, 5)" :key="'soft-'+skill" 
                          class="bg-purple-50/60 text-purple-600 border border-purple-100/60 px-2.5 py-0.5 rounded text-[11.5px] font-semibold"
                        >
                          {{ skill }}
                        </span>
                        <span v-if="role.softSkills.length > 5" class="text-gray-400 text-xs font-medium py-0.5 px-1">
                          +{{ role.softSkills.length - 5 }} more
                        </span>
                      </div>
                    </div>

                  </div>

                </div>
                
              </div>

              <!-- Pagination Controls -->
              <div v-if="totalRolePages > 1" class="mt-8 flex justify-center border-t border-gray-100 pt-5">
                <vs-pagination :total="totalRolePages" v-model="rolesCurrentPage" color="primary" />
              </div>
              
            </div>

          </div>

          <!-- AT A GLANCE SIDEBAR (Right side) -->
          <!-- At a Glance -->
<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-5 self-start h-fit">

  <!-- Header -->
  <div class="flex items-center gap-2 mb-4">
    <feather-icon
      icon="EyeIcon"
      class="w-5 h-5 text-orange-500 flex-shrink-0"
    />

    <h3 class="text-base font-semibold text-gray-900">
      At a glance
    </h3>
  </div>

  <!-- Details -->
  <div class="space-y-3">

    <!-- Public ID -->
    <div
      v-if="orgData.slug"
      class="flex items-center gap-3"
    >
      <feather-icon
        icon="AtSignIcon"
        class="w-5 h-5 text-blue-500 flex-shrink-0"
      />

      <div class="flex items-center gap-3 min-w-0">
        <span class="text-sm font-medium text-gray-600 whitespace-nowrap">
          Public ID: 
        </span>

        <span class="text-md text-gray-800 truncate">
          @{{ orgData.slug }}
        </span>
      </div>
    </div>


    <!-- Team Size -->
    <div
      v-if="orgData.employeeCount !== undefined"
      class="flex items-center gap-3"
    >
      <feather-icon
        icon="UsersIcon"
        class="w-5 h-5 text-green-500 flex-shrink-0"
      />

      <div class="flex items-center gap-3 min-w-0">
        <span class="text-sm font-medium text-gray-600 whitespace-nowrap">
          Team Size:
        </span>

        <span class="text-md text-gray-800 truncate">
          {{ orgData.employeeCount }} Active Members
        </span>
      </div>
    </div>


    <!-- Organization Structure -->
    <div
      v-if="orgData.totalRoles !== undefined && orgData.totalRoles > 0"
      class="flex items-center gap-3"
    >
      <feather-icon
        icon="LayersIcon"
        class="w-5 h-5 text-purple-500 flex-shrink-0"
      />

      <div class="flex items-center gap-3 min-w-0">
        <span class="text-sm font-medium text-gray-600 whitespace-nowrap">
          Organization Structure:
        </span>

        <span class="text-md text-gray-800 truncate">
          {{ orgData.totalRoles }} Roles Designed
        </span>
      </div>
    </div>


    <!-- Culture -->
    <div
      v-if="orgData.organizationCulture"
      class="flex items-center gap-3"
    >
      <feather-icon
        icon="HeartIcon"
        class="w-5 h-5 text-orange-500 flex-shrink-0"
      />

      <div class="flex items-center gap-3 min-w-0">
        <span class="text-sm font-medium text-gray-600 whitespace-nowrap">
          Culture:
        </span>

        <span class="text-md text-gray-800 truncate">
          {{ orgData.organizationCulture }}
        </span>
      </div>
    </div>


    <!-- Sector -->
    <div
      v-if="orgData.organizationType"
      class="flex items-center gap-3"
    >
      <feather-icon
        icon="BriefcaseIcon"
        class="w-5 h-5 text-yellow-800 flex-shrink-0"
      />

      <div class="flex items-center gap-3 min-w-0">
        <span class="text-sm font-medium text-gray-600 whitespace-nowrap">
          Sector:
        </span>

        <span class="text-md text-gray-800 truncate">
          {{ orgData.organizationType }}
        </span>
      </div>
    </div>

  </div>
</div>

        </div>
      </div>
    </main>
  </div>
</template>

<script>
import constants from "@/constants.js";

export default {
  name: "PublicOrganizationProfile",
  data() {
    return {
      constants,
      loading: true,
      error: null,
      orgData: null,
      // Add these two lines:
      rolesCurrentPage: 1,
      rolesPerPage: 5,
    };
  },
  computed: {
    paginatedRoles() {
      if (!this.orgData || !this.orgData.openRoles) return [];
      const start = (this.rolesCurrentPage - 1) * this.rolesPerPage;
      const end = start + this.rolesPerPage;
      return this.orgData.openRoles.slice(start, end);
    },
    totalRolePages() {
      if (!this.orgData || !this.orgData.openRoles) return 0;
      return Math.ceil(this.orgData.openRoles.length / this.rolesPerPage);
    }
  },
  created() {
    this.fetchPublicProfile();
  },
  methods: {
    async fetchPublicProfile() {
      this.loading = true;
      this.error = null;
      const slug = this.$route.params.slug; 

      try {
        // ONE SINGLE REQUEST to Python Backend
        const pythonApiUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${pythonApiUrl}/api/public/organization/${slug}`);
        
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Backend API returned HTML instead of JSON. Check your Python API URL.");
        }

        const result = await response.json();

        if (response.ok && result.status === 'success') {
          this.orgData = result.data;
        } else {
          this.error = result.detail || "Organization profile is private or does not exist.";
        }
      } catch (err) {
        this.error = err.message || "An error occurred while loading the profile.";
        console.error("Fetch Error:", err);
      } finally {
        this.loading = false;
      }
    },
    formatUrl(url) {
      if (!url) return '#';
      if (!url.match(/^https?:\/\//i)) {
        return 'http://' + url;
      }
      return url;
    },
    getAvatarUrl(leader) {
      // Safe fallback for user avatars
      if (leader.profilePictureUrl) {
        if (leader.profilePictureUrl.startsWith('http')) return leader.profilePictureUrl;
        // Assuming standard GetBoarded structure for user avatars:
        return `${this.constants.s3Bucket}/editor/profile-pictures/${leader.profilePictureUrl}`;
      }
      // Generate a nice initials avatar if no picture exists
      const name = encodeURIComponent(`${leader.firstName} ${leader.lastName}`);
      return `https://ui-avatars.com/api/?name=${name}&background=f1f5f9&color=64748b&bold=true`;
    }
  }
};
</script>